import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calculator, TrendingUp, CheckSquare, Sparkles, ArrowRight, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

export const ToolsOverview = () => {
  const tools = [
    {
      icon: TrendingUp,
      title: 'Live-Marktpreisrechner',
      description: 'Erhalten Sie in 60 Sekunden eine realistische Einschätzung des Marktwerts Ihrer Handelsimmobilie. Basierend auf aktuellen Transaktionsdaten und Marktpreisen mit Benchmark-Vergleich.',
      features: ['Sofortige Werteinschätzung', 'Preisspanne & €/qm', 'Rendite-Berechnung', 'Markt-Benchmark'],
      link: '/tools/marktpreis',
      status: 'Verfügbar',
      highlight: true
    },
    {
      icon: Clock,
      title: 'Verkaufszeitpunkt-Radar',
      description: 'Ermitteln Sie in 2 Minuten, ob JETZT der optimale Zeitpunkt für den Verkauf ist. Analyse von 5 Marktfaktoren mit klarer Empfehlung (VERKAUFEN / WARTEN).',
      features: ['5 Marktfaktoren', 'Ampel-System', 'Score 0-10', 'Quartalsweise Updates'],
      link: '/tools/verkaufszeitpunkt',
      status: 'Neu',
      highlight: true
    },
    {
      icon: Calculator,
      title: 'WAULT Rechner',
      description: 'Berechnen Sie kostenlos den Weighted Average Unexpired Lease Term (WAULT) Ihrer Handelsimmobilie. Der WAULT ist eine zentrale Kennzahl für Käufer und beeinflusst maßgeblich den Verkaufspreis.',
      features: ['Multi-Mieter-Unterstützung', 'Mit und ohne Optionen', 'Sofortige Bewertung', 'Detaillierte Empfehlungen'],
      link: '/tools/wault',
      status: 'Verfügbar'
    },
    {
      icon: TrendingUp,
      title: 'Retail Score Light',
      description: 'Schnelle Ersteinschätzung der Verkaufsfähigkeit Ihrer Handelsimmobilie. Erhalten Sie ein objektives Scoring von A bis D basierend auf Standort, Mieter und Mietvertrag.',
      features: ['Score A-D Bewertung', 'Top Treiber & Risiken', '3 konkrete Empfehlungen', 'Käufergruppen-Hinweis'],
      link: '/tools/retail-score',
      status: 'Verfügbar'
    },
    {
      icon: CheckSquare,
      title: 'Verkaufs-Readiness Check',
      description: 'Prüfen Sie in 2 Minuten, ob Ihre Immobilie verkaufsbereit ist. Identifizieren Sie fehlende Unterlagen und notwendige Vorbereitungsschritte für einen reibungslosen Verkauf.',
      features: ['6 Bereiche geprüft', 'To-do Liste', 'Zeitplan-Einschätzung', 'Unterlagen-Checkliste'],
      link: '/tools/readiness',
      status: 'Verfügbar'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Kostenlose Tools für Handelsimmobilien"
        description="Professionelle Analyse-Tools zur Erstbewertung: WAULT-Rechner, Retail Score, Verkaufs-Readiness Check. Kostenlos und ohne Registrierung."
        url="/tools"
      />
      <Navbar />
      <main className="flex-grow">
        {/* Hero */}
        <section className="bg-gradient-to-br from-blue-50 to-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-20 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-6 mx-auto"></div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--navy-900)' }}>
              Kostenlose Tools für Eigentümer
            </h1>
            <p className="text-lg leading-relaxed max-w-3xl mx-auto mb-8" style={{ color: 'var(--gray-700)' }}>
              Professionelle Analyse-Tools zur Erstbewertung Ihrer Handelsimmobilie. 
              Ohne Registrierung, ohne versteckte Kosten.
            </p>
          </div>
        </section>

        {/* Tools */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-12">
              {tools.map((tool, index) => {
                const IconComponent = tool.icon;
                return (
                  <Card key={index} className={`glass-card p-10 ${tool.highlight ? 'border-t-4' : ''}`} style={tool.highlight ? { borderTopColor: 'var(--gold-600)' } : {}}>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                      {/* Left: Icon + Title */}
                      <div className="lg:col-span-1">
                        <div className="flex items-start gap-4">
                          <div className="w-16 h-16 rounded-xl flex items-center justify-center shadow-lg" style={{ background: tool.highlight ? 'linear-gradient(135deg, #D4A853 0%, #F0C870 100%)' : 'linear-gradient(135deg, #0F2744 0%, #2563A0 100%)' }}>
                            <IconComponent className="h-8 w-8 text-white" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="text-2xl font-bold" style={{ color: 'var(--navy-800)' }}>
                                {tool.title}
                              </h3>
                              {tool.highlight && <Sparkles className="h-5 w-5" style={{ color: 'var(--gold-600)' }} />}
                            </div>
                            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${tool.status === 'Verfügbar' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {tool.status}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Middle: Description + Features */}
                      <div className="lg:col-span-1">
                        <p className="mb-4" style={{ color: 'var(--gray-700)' }}>
                          {tool.description}
                        </p>
                        <ul className="space-y-2">
                          {tool.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: 'var(--gray-600)' }}>
                              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--gold-600)' }}></div>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Right: CTA */}
                      <div className="lg:col-span-1 flex items-center">
                        {tool.status === 'Verfügbar' ? (
                          <Link to={tool.link} className="w-full">
                            <Button className={`w-full py-6 font-semibold ${tool.highlight ? 'btn-gold-gradient text-white border-0' : ''}`} style={!tool.highlight ? { backgroundColor: 'var(--navy-600)', color: 'white' } : {}}>
                              Tool jetzt nutzen
                              <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                          </Link>
                        ) : (
                          <div className="w-full text-center p-6 bg-gray-50 rounded-lg">
                            <p className="text-sm font-semibold" style={{ color: 'var(--gray-600)' }}>Dieses Tool wird in Kürze verfügbar sein.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Info */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-lg" style={{ color: 'var(--gray-700)' }}>
              Alle Tools können <strong style={{ color: 'var(--navy-800)' }}>anonym und ohne Registrierung</strong> genutzt werden. 
              Ihre Eingaben werden nur mit Ihrer ausdrücklichen Einwilligung gespeichert.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ToolsOverview;